// ---------- OverviewPanel + VisionMissionCard (paste into ProgrammePage file) ----------
import { useEffect as useEff } from "react";
import VisionMissionCard from "./VisionMissionCard";

const OverviewPanel = ({ programmeInfo = {} }) => {
  useEff(() => {
    const id = "progov-styles-v2";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
/* ---- progov injected styles (vision/mission as cards + overview layout) ---- */
:root {
  --progov-green: #17a046;
  --progov-red: #ee464b;
  --progov-text: #071724;
  --progov-muted: rgba(6,16,34,0.04);
}

.progov-root { color: var(--progov-text); font-family: Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Arial; }
.progov-constrain { max-width:1200px; margin:18px auto; padding:12px; }

/* lead block uses green accent */
.progov-lead {
  background: linear-gradient(180deg, rgba(23,160,70,0.04), rgba(255,255,255,0));
  border-left: 6px solid var(--progov-green);
  padding: 26px;
  border-radius: 14px;
  margin-bottom: 20px;
  box-shadow: 0 8px 30px rgba(4,12,28,0.04);
}
.progov-lead p { margin:0 0 12px 0; line-height:1.74; font-size:1.02rem; color:#07243a; }

.progov-columns { display:grid; grid-template-columns:1fr 360px; gap:20px; align-items:flex-start; }
.progov-article { display:flex; flex-direction:column; gap:14px; }
.progov-paragraph {
  background:#fff;
  padding:14px;
  border-radius:12px;
  border:1px solid var(--progov-muted);
  box-shadow:0 10px 30px rgba(6,18,36,0.04);
}
.progov-paragraph p { margin:0; line-height:1.7; color:#233945; }

/* aside cards */
.progov-aside { position:sticky; top:92px; display:flex; flex-direction:column; gap:14px; }
.progov-card {
  background:linear-gradient(180deg,#fff,#fbfdff);
  border-radius:12px;
  padding:14px;
  border:1px solid var(--progov-muted);
  box-shadow:0 12px 36px rgba(3,12,30,0.04);
}
.progov-card h4 {
  margin:0 0 8px 0;
  font-size:0.85rem;
  letter-spacing:0.08em;
  color: var(--progov-green);
  text-transform:uppercase;
  font-weight:800;
}
.progov-card p { margin:0; color:#243d4a; line-height:1.6; font-size:0.95rem; }

/* Vision / Mission card base */
.progov-vm-card {
  display:flex;
  gap:14px;
  align-items:flex-start;
  padding:14px;
  border-radius:12px;
  background:linear-gradient(180deg,#fff,#fbfdff);
  border:1px solid var(--progov-muted);
  box-shadow:0 12px 36px rgba(3,12,30,0.04);
  transition:transform 220ms, box-shadow 220ms;
}
.progov-vm-card:hover { transform:translateY(-6px); box-shadow:0 22px 60px rgba(6,20,40,0.10); }
.progov-vm-left { flex:0 0 56px; display:flex; align-items:center; justify-content:center; }
.progov-vm-icon {
  width:48px; height:48px; border-radius:10px; display:grid; place-items:center;
  color: var(--progov-green);
  background: linear-gradient(180deg, rgba(23,160,70,0.07), rgba(255,255,255,0.6));
  box-shadow:0 8px 20px rgba(4,12,24,0.06);
}
.progov-vm-right { flex:1; }
.progov-vm-heading {
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.06em;
  color:#053a75;
  font-size:0.82rem;
  margin-bottom:8px;
}
.progov-vm-copy p { margin:0 0 8px 0; line-height:1.6; color:#1f3a45; font-size:0.95rem; }

/* Accent mappings: keep backwards compatible names */
.progov-vm-card--blue .progov-vm-icon { color: var(--progov-green); background: linear-gradient(180deg, rgba(23,160,70,0.08), rgba(255,255,255,0.6)); }
.progov-vm-card--teal .progov-vm-icon { color: var(--progov-red); background: linear-gradient(180deg, rgba(238,70,75,0.08), rgba(255,255,255,0.6)); }

/* explicit new names if you prefer to use them later */
.progov-vm-card--green .progov-vm-icon { color: var(--progov-green); background: linear-gradient(180deg, rgba(23,160,70,0.08), rgba(255,255,255,0.6)); }
.progov-vm-card--red .progov-vm-icon { color: var(--progov-red); background: linear-gradient(180deg, rgba(238,70,75,0.08), rgba(255,255,255,0.6)); }

/* core values using green */
.progov-corevalues { display:flex; flex-direction:column; gap:8px; margin-top:6px; }
.progov-corevalues .cv-item {
  display:flex; gap:10px; align-items:center; padding:8px 10px; border-radius:10px;
  background: linear-gradient(180deg, rgba(23,160,70,0.03), rgba(255,255,255,0));
  border:1px solid rgba(23,160,70,0.06);
  color: #0f5035; font-weight:600;
}

/* objectives */
.progov-objectives { margin-top:18px; display:flex; flex-direction:column; gap:12px; }
.progov-obj-grid { display:grid; grid-template-columns: repeat(2,1fr); gap:12px; }
.progov-obj {
  display:flex; gap:12px; background:#fff; padding:12px; border-radius:12px; border:1px solid var(--progov-muted); align-items:flex-start;
}
.progov-obj .idx {
  width:40px; height:40px; border-radius:8px;
  background: linear-gradient(180deg, rgba(23,160,70,0.06), rgba(255,255,255,0));
  display:grid; place-items:center; font-weight:800; color: var(--progov-green);
}
.progov-obj .txt { line-height:1.6; color:#23414a; }

/* small utility tweaks */
.progov-meta { display:flex; gap:10px; flex-wrap:wrap; margin-top:8px; color:#527581; font-size:0.92rem; }
.progov-hr { height:1px; background: linear-gradient(90deg, rgba(23,160,70,0.08), rgba(23,160,70,0.02)); margin:10px 0; border-radius:1px; }

/* responsive */
@media(max-width:980px){ 
  .progov-columns{ grid-template-columns:1fr; } 
  .progov-obj-grid{ grid-template-columns:1fr; } 
  .progov-aside{ position:relative; top:auto; } 
}
`;

    document.head.appendChild(style);
    // eslint-disable-next-line no-console
    console.log("OverviewPanel mounted — progov styles injected");
  }, [programmeInfo]);

  // normalize paragraphs (overview is an array per your JSON)
  const paragraphs = Array.isArray(programmeInfo.overview)
    ? programmeInfo.overview
    : typeof programmeInfo.overview === "string"
      ? programmeInfo.overview.split(/\n{2,}/).map((p) => p.trim())
      : [];

  // quick facts
  const quick = [
    { label: "Duration", val: programmeInfo.duration || "N/A" },
    { label: "Fee", val: programmeInfo.fee ? `₦${programmeInfo.fee}` : "N/A" },
  ];

  // debug log
  // eslint-disable-next-line no-console
  console.log("OverviewPanel rendering — paragraphs:", paragraphs.length);

  return (
    <div className="progov-root">
      <div className="progov-constrain">
        {/* lead */}
        {paragraphs[0] && (
          <div className="progov-lead">
            {paragraphs[0].split("\n").map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        )}

        <div className="progov-columns">
          <div className="progov-article">
            {paragraphs.slice(1).map((p, i) => (
              <article className="progov-paragraph" key={i}>
                {p.split("\n").map((ln, j) => (
                  <p key={j}>{ln}</p>
                ))}
              </article>
            ))}

            {/* objectives */}
            {Array.isArray(programmeInfo.objectives) &&
              programmeInfo.objectives.length > 0 && (
                <section
                  className="progov-objectives"
                  aria-labelledby="obj-heading"
                >
                  <h3 id="obj-heading" style={{ margin: 0, color: "#0b3b5b" }}>
                    Objectives
                  </h3>
                  <div style={{ height: 8 }} />
                  <div className="progov-obj-grid">
                    {programmeInfo.objectives.map((o, idx) => (
                      <div className="progov-obj" key={idx}>
                        <div className="idx">{idx + 1}</div>
                        <div className="txt">{o}</div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
          </div>

          <aside className="progov-aside">
            {/* Vision card */}
            {programmeInfo.vision && (
              <div className="progov-card">
                <VisionMissionCard
                  title="Vision"
                  body={programmeInfo.vision}
                  accent="blue"
                  icon="vision"
                />
              </div>
            )}

            {/* Mission card */}
            {programmeInfo.mission && (
              <div className="progov-card">
                <VisionMissionCard
                  title="Mission"
                  body={programmeInfo.mission}
                  accent="teal"
                  icon="mission"
                />
              </div>
            )}

            {/* Quick facts */}
            <div className="progov-card">
              <h4 style={{ margin: "0 0 10px 0" }}>Quick facts</h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {quick.map((q, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#157019ff",
                    }}
                  >
                    <strong>{q.label}</strong>
                    <span>{q.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Core values */}
            {Array.isArray(programmeInfo.coreValues) &&
              programmeInfo.coreValues.length > 0 && (
                <div className="progov-card">
                  <h4 style={{ margin: "0 0 10px 0" }}>Core values</h4>
                  <div className="progov-corevalues">
                    {programmeInfo.coreValues.map((cv, i) => (
                      <div className="cv-item" key={i}>
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 99,
                            background: "#00d4ff",
                            marginRight: 8,
                          }}
                        />
                        <div style={{ fontWeight: 700 }}>{cv}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default OverviewPanel;
